import React, { useState, useEffect } from 'react';
import { MessageCircle, Facebook, Send, Instagram, Share2 } from 'lucide-react';
import { buildShareUrl } from '../utils/shareUrl';

export default function ShareButtons({ product, productUrl, shop, kind = 'product', resourceId }) {
  // resolvedUrl is the user-facing share URL, computed asynchronously when shop is provided.
  // Falls back to productUrl (legacy) so existing callers keep working.
  const [resolvedUrl, setResolvedUrl] = useState(productUrl || '');

  useEffect(() => {
    let mounted = true;
    if (shop && (resourceId || kind === 'shop')) {
      buildShareUrl({ shop, kind, id: resourceId }).then(u => {
        if (mounted) setResolvedUrl(u);
      });
    } else if (productUrl) {
      setResolvedUrl(productUrl);
    }
    return () => { mounted = false; };
  }, [shop?.id, shop?.custom_domain, shop?.slug, kind, resourceId, productUrl]);

  const shareUrl = resolvedUrl || productUrl || (typeof window !== 'undefined' ? window.location.href : '');
  const text = `${product.title} - ${product.price?.toFixed(2)}€`;
  const encodedText = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(shareUrl);

  const shareNative = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: product.title, text: `${text}\n`, url: shareUrl });
      } catch { /* user cancelled */ }
    } else {
      navigator.clipboard?.writeText(shareUrl);
    }
  };

  const channels = [
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      color: '#25D366',
      url: `https://wa.me/?text=${encodedText}%0A${encodedUrl}`,
      testId: 'share-whatsapp'
    },
    {
      name: 'Facebook',
      icon: Facebook,
      color: '#1877F2',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
      testId: 'share-facebook'
    },
    {
      name: 'Telegram',
      icon: Send,
      color: '#0088cc',
      url: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
      testId: 'share-telegram'
    },
    {
      name: 'Instagram',
      icon: Instagram,
      color: '#E4405F',
      url: null,
      testId: 'share-instagram',
      action: () => {
        navigator.clipboard?.writeText(`${text}\n${shareUrl}`);
        alert('Link copiato! Incollalo su Instagram.');
      }
    },
    {
      name: 'TikTok',
      icon: () => (
        <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
          <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 00-.79-.05A6.34 6.34 0 003.15 15.2a6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.34-6.34V8.69a8.26 8.26 0 004.76 1.5v-3.5a4.85 4.85 0 01-1-.01z" />
        </svg>
      ),
      color: '#000000',
      url: null,
      testId: 'share-tiktok',
      action: () => {
        navigator.clipboard?.writeText(`${text}\n${shareUrl}`);
        alert('Link copiato! Incollalo su TikTok.');
      }
    },
    {
      name: 'Condividi',
      icon: Share2,
      color: '#C25934',
      url: null,
      testId: 'share-native',
      action: shareNative
    }
  ];

  return (
    <div className="flex flex-wrap gap-2">
      {channels.map(ch => {
        const Icon = ch.icon;
        return (
          <button
            key={ch.name}
            data-testid={ch.testId}
            onClick={(e) => {
              e.stopPropagation();
              if (ch.action) { ch.action(); }
              else if (ch.url) { window.open(ch.url, '_blank', 'noopener'); }
            }}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full text-white text-xs font-medium transition-all duration-300 hover:scale-105 active:scale-95"
            style={{ backgroundColor: ch.color }}
            title={ch.name}
          >
            <Icon size={16} />
            <span className="hidden sm:inline">{ch.name}</span>
          </button>
        );
      })}
    </div>
  );
}
